import React, { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BankPaymentLoader from "@/components/ui/bankPayment/BankPaymentLoader";
import BankTransactionErrorCard from "@/components/ui/bankPayment/BankTransactionErrorCard";
import { useProcessAlfalahCallbackMutation } from "@/services/bankPaymentApi";

const AlfalahOrderStatusCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [processAlfalah, { isLoading, isError, error: mutationError }] =
    useProcessAlfalahCallbackMutation();

  const orderId = useMemo(() => {
    return (searchParams.get("O") || "").trim();
  }, [searchParams]);

  useEffect(() => {
    const handleCallback = async () => {
      if (!orderId) return;

      try {
        const result = await processAlfalah({ order_id: orderId }).unwrap();

        const transactionStatus = (result.status || "").toUpperCase().trim();
        const isProcessed = result.processed === true;
        const isPaidStatus = ["PAID", "SUCCESS", "CAPTURED"].includes(
          transactionStatus
        );

        if (!isProcessed || !isPaidStatus) {
          navigate("/payment/failed", {
            replace: true,
            state: {
              error: {
                error: true,
                title: "Payment Failed",
                message:
                  result.message ||
                  `Transaction status: ${result.status || "Unknown"}`,
              },
            },
          });
          return;
        }

        navigate("/payment/success", {
          replace: true,
          state: {
            transaction_id: result.order_id || orderId,
            transaction_status: result.status,
            message: result.message,
            callback_data: result.data,
          },
        });
      } catch (err: any) {
        console.error("Alfalah Callback Error:", err);
      }
    };

    void handleCallback();
  }, [navigate, orderId, processAlfalah]);

  if (isLoading) {
    return <BankPaymentLoader />;
  }

  if (!orderId) {
    return (
      <BankTransactionErrorCard
        title="Invalid Callback URL"
        message="Missing order_id in callback URL."
      />
    );
  }

  if (isError) {
    const errorMsg =
      (mutationError as any)?.data?.message ||
      (mutationError as any)?.data?.detail ||
      "Failed to process Alfalah callback response.";

    return (
      <BankTransactionErrorCard
        title="Callback Processing Error"
        message={errorMsg}
        onRetry={() => {
          void processAlfalah({ order_id: orderId });
        }}
      />
    );
  }

  return <BankPaymentLoader />;
};

export default AlfalahOrderStatusCallbackPage;